import { canonicalStyleClass } from './bayer-vocabulary'

/**
 * What: Removes class tokens outside the Bayer Named Style vocabulary from a
 *       live DOM sub-tree.
 * Why:  The LLM may emit class names it invented, echoed from a context snippet
 *       in an unexpected form, or hallucinated entirely. Unknown classes are
 *       harmless in the bubble (no matching CSS) but cause Word to apply a
 *       blank / default style on insert instead of letting the element to fall
 *       back to the surrounding style context naturally.
 * How:  For each element that has a class attribute, filter its tokens to the
 *       known vocabulary; remove the attribute entirely when none survive.
 *       All inline styles (including mso-*) are left untouched — they carry
 *       load-bearing rendering information.
 */
export function validateAgainstVocabularyOnDom(root: ParentNode): void {
  root.querySelectorAll('[class]').forEach((el) => {
    const raw = el.getAttribute('class') ?? ''
    const tokens = raw.split(/\s+/).filter(Boolean)
    // Case-insensitive lookup: accept wrong-cased tokens and normalise to canonical
    // form so CSS selectors (e.g. p.BayerBodyTextFull) still match after rewrite.
    const kept = tokens.map((t) => canonicalStyleClass(t)).filter((t): t is string => t !== null)

    if (kept.length === 0) {
      el.removeAttribute('class')
    } else {
      // Always rewrite: normalises casing even when count is unchanged
      el.setAttribute('class', kept.join(' '))
    }
  })
}
